import { ShiftProfile, ShiftProfileCreate } from 'repository/schedule/@types/ShiftProfile';
// Cache manejado por AxiosHttpService; este repositorio no interactúa directamente con el caché

// La caché y ETag son manejados internamente por AxiosHttpService

interface ShiftProfileApiItem {
    shift_profile_id: number;
    shift_profile_name: string;
    shift_profile_description?: string | null;
    is_night: boolean;
    min_start_time: string;
    max_start_time: string;
    min_end_time: string;
    max_end_time: string;
    standard_duration: string;
    min_duration: string;
    max_duration: string;
}

interface ShiftProfileApiResponse {
    status: string;
    data: ShiftProfileApiItem[];
}

interface ShiftProfileApiOneResponse {
    status: string;
    data: ShiftProfileApiItem;
}

const normalizeTime = (value: string) => value.slice(0, 5);
const toApiTime = (value: string) => (value.length === 5 ? `${value}:00` : value);

const buildUpsertPayload = (data: ShiftProfileCreate | ShiftProfile): ShiftProfileCreate => ({
    shift_profile_name: data.shift_profile_name,
    shift_profile_description: data.shift_profile_description ?? '',
    is_night: data.is_night,
    min_start_time: toApiTime(data.min_start_time),
    max_start_time: toApiTime(data.max_start_time),
    min_end_time: toApiTime(data.min_end_time),
    max_end_time: toApiTime(data.max_end_time),
    standard_duration: toApiTime(data.standard_duration),
    min_duration: toApiTime(data.min_duration),
    max_duration: toApiTime(data.max_duration),
});

export class ShiftProfileRepository {
    private httpService: IHttpService;
    private baseUrl: string;


    constructor(httpService: IHttpService) {
        this.httpService = httpService;
        this.baseUrl = '/shift_profiles';
    }

    async getAll(): Promise<ShiftProfile[]> {
        const response = await this.httpService.get<ShiftProfileApiResponse>(this.baseUrl);

        return (response.data ?? [])
            .map((item) => ({
                ...item,
                id: item.shift_profile_id,
                shift_profile_description: item.shift_profile_description ?? '',
                min_start_time: normalizeTime(item.min_start_time),
                max_start_time: normalizeTime(item.max_start_time),
                min_end_time: normalizeTime(item.min_end_time),
                max_end_time: normalizeTime(item.max_end_time),
                standard_duration: normalizeTime(item.standard_duration),
                min_duration: normalizeTime(item.min_duration),
                max_duration: normalizeTime(item.max_duration),
            }))
            .sort((a, b) => a.shift_profile_name.localeCompare(b.shift_profile_name));
    }

    async getOne(id: number): Promise<ShiftProfile> {
        const response = await this.httpService.get<ShiftProfileApiOneResponse>(
            `${this.baseUrl}/${encodeURIComponent(String(id))}`,
        );
        const item = response.data;

        if (!item) {
            throw new Error(`ShiftProfile ${id} not found`);
        }

        return {
            ...item,
            id: item.shift_profile_id,
            shift_profile_description: item.shift_profile_description ?? '',
            min_start_time: normalizeTime(item.min_start_time),
            max_start_time: normalizeTime(item.max_start_time),
            min_end_time: normalizeTime(item.min_end_time),
            max_end_time: normalizeTime(item.max_end_time),
            standard_duration: normalizeTime(item.standard_duration),
            min_duration: normalizeTime(item.min_duration),
            max_duration: normalizeTime(item.max_duration),
        };
    }

    async create(data: ShiftProfileCreate): Promise<ShiftProfile> {
        const payload = buildUpsertPayload(data);
        return await this.httpService.post<ShiftProfile, ShiftProfileCreate>(this.baseUrl, { data: payload });
    }

    async update(id: number, data: ShiftProfile): Promise<ShiftProfile> {
        const payload = buildUpsertPayload(data);
        return await this.httpService.patch<ShiftProfile, ShiftProfileCreate>(
            `${this.baseUrl}/${encodeURIComponent(String(id))}`,
            { data: payload },
        );
    }

    async delete(id: number): Promise<void> {
        await this.httpService.delete<void>(`${this.baseUrl}/${encodeURIComponent(String(id))}`);
    }
}

